import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

const PAGE_SIZE = 100;

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function FiltroProcesos({
  initialFilters = { nombre: "" },
  onSubmit = () => {},
  onClose = () => {},
}) {
  // ===== Autocomplete =====
  const [nombreInput, setNombreInput] = useState(initialFilters.nombre || "");
  const debNombre = useDebounced(nombreInput, 300);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const boxRef = useRef(null);
  const drawerRef = useRef(null);

  // ===== ESC para cerrar =====
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ===== Click afuera del autocomplete =====
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // ===== Cargar página 0 =====
  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      try {
        const data = await apiFetchJson(
          apiRoutes.procesos.autocompleteUrl({
            page: 0,
            size: PAGE_SIZE,
            q: debNombre,
          }),
        );

        setItems(data?.items ?? []);
        setPage(data?.page ?? 0);
        setHasMore(Boolean(data?.hasMore));
      } catch (e) {
        console.error("Autocomplete procesos:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [debNombre, open]);

  // ===== Scroll infinito =====
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (!hasMore || loading) return;

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      const next = page + 1;
      setLoading(true);

      apiFetchJson(
        apiRoutes.procesos.autocompleteUrl({
          page: next,
          size: PAGE_SIZE,
          q: debNombre,
        }),
      )
        .then((data) => {
          setItems((prev) => [...prev, ...(data?.items ?? [])]);
          setPage(data?.page ?? next);
          setHasMore(Boolean(data?.hasMore));
        })
        .catch((e) => console.error("Scroll procesos:", e))
        .finally(() => setLoading(false));
    }
  };

  // ===== Submit =====
  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      nombre: nombreInput.trim(),
    });
    onClose();
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div
        className="drawer-panel overflow-auto"
        ref={drawerRef}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button className="btn-cancel" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Body */}
        <form onSubmit={submit} className="drawer-body space-y-4">
          {/* NOMBRE */}
          <div ref={boxRef} className="relative">
            <label className="form-label-fil">Nombre del proceso:</label>
            <input
              type="text"
              className="input"
              placeholder="Buscar proceso..."
              value={nombreInput}
              onChange={(e) => {
                setNombreInput(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />

            {open && (
              <div
                className="absolute z-50 mt-1 w-full border rounded bg-white shadow-xl max-h-56 overflow-auto"
                onScroll={onScroll}
              >
                {items.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => {
                      setNombreInput(p.codigo ?? p.nombre ?? "");
                      setOpen(false);
                    }}
                  >
                    {p.codigo ?? p.nombre}
                  </button>
                ))}

                {loading && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Cargando…
                  </div>
                )}

                {!loading && items.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    Sin resultados
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-right">
            <button type="submit" className="btn">
              Aplicar Filtro
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
